import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingDown,
  CreditCard,
  Users,
  Building2,
  Bell,
  RefreshCw,
  ChevronRight
} from 'lucide-react';

interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  action?: string;
  actionLink?: string;
  timestamp: Date;
}

interface AlertsPanelProps {
  compact?: boolean;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ compact = false }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateAlerts();
  }, []);

  const generateAlerts = async () => {
    setLoading(true);
    const generatedAlerts: Alert[] = [];

    try {
      // 1. Verificar negocios sin actividad reciente (7 días)
      const businessesSnap = await getDocs(collection(db, 'businesses'));
      const businesses = businessesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const inactiveBusinesses = businesses.filter((b: any) => {
        const lastUpdate = b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0);
        return lastUpdate < sevenDaysAgo;
      });

      if (inactiveBusinesses.length > 0) {
        generatedAlerts.push({
          id: 'inactive-businesses',
          type: 'warning',
          title: 'Negocios sin actividad',
          message: `${inactiveBusinesses.length} negocio(s) sin actividad en los últimos 7 días`,
          action: 'Ver negocios',
          actionLink: '/super/businesses',
          timestamp: new Date()
        });
      }

      // 2. Verificar usuarios pendientes de aprobación
      const pendingUsersQuery = query(
        collection(db, 'users'),
        where('status', '==', 'pending_approval')
      );
      const pendingUsersSnap = await getDocs(pendingUsersQuery);
      
      if (pendingUsersSnap.size > 0) {
        generatedAlerts.push({
          id: 'pending-users',
          type: 'info',
          title: 'Usuarios pendientes',
          message: `${pendingUsersSnap.size} usuario(s) esperando aprobación`,
          action: 'Ver usuarios',
          actionLink: '/super/users?status=pending_approval',
          timestamp: new Date()
        });
      }

      // 3. Verificar negocios en plan gratuito (oportunidad de conversión)
      const freeBusinesses = businesses.filter((b: any) => 
        !b.subscription?.plan || b.subscription.plan === 'free'
      );
      const paidBusinesses = businesses.filter((b: any) => 
        b.subscription?.plan && b.subscription.plan !== 'free'
      );

      if (freeBusinesses.length > paidBusinesses.length) {
        generatedAlerts.push({
          id: 'free-majority',
          type: 'warning',
          title: 'Mayoría en plan gratuito',
          message: `${freeBusinesses.length} de ${businesses.length} negocios en plan gratuito`,
          action: 'Ver distribución',
          actionLink: '/super/businesses?plan=free',
          timestamp: new Date()
        });
      }

      // 4. Verificar negocios suspendidos
      const suspendedBusinesses = businesses.filter((b: any) => 
        b.subscription?.status === 'suspended'
      );

      if (suspendedBusinesses.length > 0) {
        generatedAlerts.push({
          id: 'suspended-businesses',
          type: 'error',
          title: 'Negocios suspendidos',
          message: `${suspendedBusinesses.length} negocio(s) actualmente suspendido(s)`,
          action: 'Ver suspendidos',
          actionLink: '/super/businesses?status=suspended',
          timestamp: new Date()
        });
      }

      // 5. Verificar compras recientes (últimas 24h)
      try {
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        
        const recentPurchasesQuery = query(
          collection(db, 'purchases'),
          where('createdAt', '>=', oneDayAgo),
          limit(10)
        );
        const recentPurchasesSnap = await getDocs(recentPurchasesQuery);
        
        if (recentPurchasesSnap.size > 0) {
          const totalRevenue = recentPurchasesSnap.docs.reduce((sum, doc) => {
            return sum + ((doc.data().amount || 0) / 100);
          }, 0);

          generatedAlerts.push({
            id: 'recent-purchases',
            type: 'success',
            title: 'Compras recientes',
            message: `${recentPurchasesSnap.size} compra(s) en las últimas 24h (€${totalRevenue.toFixed(2)})`,
            timestamp: new Date()
          });
        }
      } catch (e) {
        // Colección puede no existir
      }

      // 6. Verificar negocios sin Stripe Connect
      const noStripeBusinesses = businesses.filter((b: any) => 
        !b.stripeConnectStatus || b.stripeConnectStatus !== 'active'
      );

      if (noStripeBusinesses.length > 0 && noStripeBusinesses.length < businesses.length) {
        generatedAlerts.push({
          id: 'no-stripe',
          type: 'info',
          title: 'Sin Stripe Connect',
          message: `${noStripeBusinesses.length} negocio(s) sin pagos online configurados`,
          action: 'Ver negocios',
          actionLink: '/super/businesses',
          timestamp: new Date()
        });
      }

      // 7. Si todo está bien
      if (generatedAlerts.length === 0) {
        generatedAlerts.push({
          id: 'all-good',
          type: 'success',
          title: 'Sistema operativo',
          message: 'No hay alertas activas. Todo funciona correctamente.',
          timestamp: new Date()
        });
      }

      // Ordenar por prioridad (error > warning > info > success)
      const priority = { error: 0, warning: 1, info: 2, success: 3 };
      generatedAlerts.sort((a, b) => priority[a.type] - priority[b.type]);

      setAlerts(generatedAlerts);
    } catch (e) {
      console.error('Error generando alertas:', e);
      setAlerts([{
        id: 'error',
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar las alertas',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'info':
        return <Bell className="w-5 h-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getAlertBgClass = (type: Alert['type']) => {
    switch (type) {
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
      case 'info':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  if (compact) {
    // Vista compacta para el dashboard
    return (
      <div className="space-y-2">
        {alerts.slice(0, 3).map(alert => (
          <div
            key={alert.id}
            className={`flex items-center gap-3 p-3 rounded-lg border ${getAlertBgClass(alert.type)}`}
          >
            {getAlertIcon(alert.type)}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {alert.title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {alert.message}
              </p>
            </div>
            {alert.actionLink && (
              <a
                href={alert.actionLink}
                className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                Ver
                <ChevronRight className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
        {alerts.length > 3 && (
          <p className="text-xs text-center text-gray-500 py-1">
            +{alerts.length - 3} alertas más
          </p>
        )}
      </div>
    );
  }

  // Vista completa
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Alertas del Sistema
        </h3>
        <button
          onClick={generateAlerts}
          className="text-sm text-gray-500 hover:text-amber-600 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {alerts.map(alert => (
        <div
          key={alert.id}
          className={`flex items-start gap-3 p-4 rounded-xl border ${getAlertBgClass(alert.type)}`}
        >
          <div className="flex-shrink-0 mt-0.5">
            {getAlertIcon(alert.type)}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-white">
              {alert.title}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {alert.message}
            </p>
            {alert.action && alert.actionLink && (
              <a
                href={alert.actionLink}
                className="inline-flex items-center gap-1 mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                {alert.action}
                <ChevronRight className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlertsPanel;











